import React, { useState, useRef, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import SpiralTorus3D from "../components/SpiralTorus3D";

export default function SpiralTorusPage() {
  // États entropie
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  // Contrôles
  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  const [spiralSpeedPct, setSpiralSpeedPct] = useState(50);
  const [flashRatePct, setFlashRatePct] = useState(30);

  // Sliders Bloom
  const [bloomIntensity, setBloomIntensity] = useState(1.2);
  const [bloomRadius, setBloomRadius] = useState(0.6);
  const [bloomThreshold, setBloomThreshold] = useState(0.0);

  const canvasRef = useRef(null);
  const orbitRef = useRef(null);

  // Dernier instantané de scène remonté par SpiralTorus3D. Stocké dans un ref
  // et non dans un état : il est réécrit à chaque frame, un état déclencherait
  // un rendu par frame pour une donnée que seul le POST consomme.
  const snapshotRef = useRef(null);
  const debounceRef = useRef(null);

  // --- Entropie UI : l'INTENTION DE L'OPÉRATEUR, et rien d'autre ---
  //
  // ⚠️ Ce POST transportait aussi `positions` et `velocities`, et la route en
  // tirait une « entropie dynamique » qu'elle renvoyait dans la même réponse.
  // Les deux canaux sont désormais séparés : `ui` ici, `dynamic` par
  // `/dyn_entropy` ci-dessous. Voir docs/DOCTRINE_ENTROPIE_PHYSIQUE.md.
  useEffect(() => {
    setLoadingUI(true);
    const controller = new AbortController();

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetch("/api/geometry/spiral_torus/entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chaos,
          glow,
          spiral_speed: spiralSpeedPct / 100,
          flash_rate: flashRatePct / 100,
          bloom_intensity: bloomIntensity,
          bloom_radius: bloomRadius,
          bloom_threshold: bloomThreshold,
        }),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          if (typeof data.ui_entropy === "number") setEntropyUI(data.ui_entropy);
        })
        .catch(() => setEntropyUI(null))
        .finally(() => setLoadingUI(false));
    }, 300);

    return () => {
      clearTimeout(debounceRef.current);
      controller.abort();
    };
  }, [
    chaos,
    glow,
    spiralSpeedPct,
    flashRatePct,
    bloomIntensity,
    bloomRadius,
    bloomThreshold,
  ]);

  // --- Entropie dynamique : l'état PHYSIQUE de la scène affichée ---
  //
  // Cadence fixe plutôt qu'un envoi par frame : l'instantané porte 200
  // positions, 200 vitesses et 200 couleurs. La valeur affichée était
  // auparavant calculée dans le navigateur, à partir de tangentes de courbe
  // pondérées par les curseurs de bloom.
  useEffect(() => {
    let actif = true;
    const controller = new AbortController();

    const envoyer = () => {
      const snap = snapshotRef.current;
      if (!snap || !snap.positions || snap.positions.length === 0) return;

      fetch("/api/geometry/spiral_torus/dyn_entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(snap),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          if (actif && typeof data.entropy_dyn === "number") {
            setEntropyDyn(data.entropy_dyn);
          }
        })
        .catch(() => {});
    };

    const id = setInterval(envoyer, 1000);
    return () => {
      actif = false;
      clearInterval(id);
      controller.abort();
    };
  }, []);

  // ⚡ Score total
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div
      className="page-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <h1>Spiral Torus Visualizer</h1>

      {/* Jauges */}
      <div style={{ display: "flex", gap: 36, marginBottom: 22 }}>
        <div className="card" style={{ minWidth: 170 }}>
          <h2>Entropie UI</h2>
          <div style={{ fontSize: "1.2rem", color: "#03dac6" }}>
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 170 }}>
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div style={{ fontSize: "1.2rem", color: "#ff0266" }}>
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 180 }}>
          <h2 style={{ color: "#bb86fc" }}>Score Total</h2>
          <div style={{ fontSize: "1.2rem", color: "#bb86fc" }}>
            {entropyTotal}
          </div>
        </div>
      </div>

      {/* Contrôles + Canvas */}
      <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
        {/* Contrôles */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            minWidth: 260,
          }}
        >
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: chaos ? "#ff0266" : "#03dac6",
              fontWeight: "bold",
            }}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: glow ? "#ffd600" : "#666",
              fontWeight: "bold",
            }}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          {/* Sliders classiques */}
          <div className="input-group">
            <label>Vitesse spirale : {spiralSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={spiralSpeedPct}
              onChange={(e) => setSpiralSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Flash : {flashRatePct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={flashRatePct}
              onChange={(e) => setFlashRatePct(Number(e.target.value))}
            />
          </div>

          {/* Sliders Bloom */}
          <div className="input-group">
            <label>Bloom Intensity : {bloomIntensity.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={3}
              step={0.1}
              value={bloomIntensity}
              onChange={(e) => setBloomIntensity(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Bloom Radius : {bloomRadius.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={bloomRadius}
              onChange={(e) => setBloomRadius(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Bloom Threshold : {bloomThreshold.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={bloomThreshold}
              onChange={(e) => setBloomThreshold(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Canvas */}
        <div
          className="card"
          style={{ width: 600, height: 480, background: "#181818" }}
        >
          {/*
            Caméra reculée de 25 à 34. L'arbitrage C2 porte le tore de R=3 à
            R=8 : l'objet passe d'un rayon apparent de ~3,5 à 10, et débordait
            du cadre au réglage précédent. On ajuste le point de vue, pas la
            scène — c'est la caméra qui s'adapte à l'objet, jamais l'inverse.
          */}
          <Canvas
            ref={canvasRef}
            camera={{ position: [0, 9, 34], fov: 60 }}
            style={{ width: 600, height: 480, borderRadius: "10px" }}
          >
            <ambientLight intensity={0.7} />
            <pointLight position={[30, 40, 30]} intensity={1.2} />
            <SpiralTorus3D
              running={running}
              chaos={chaos}
              glow={glow}
              spiralSpeedPct={spiralSpeedPct}
              flashRatePct={flashRatePct}
              bloomIntensity={bloomIntensity}
              bloomRadius={bloomRadius}
              bloomThreshold={bloomThreshold}
              // Conserve le dernier instantané pour le prochain POST. Le
              // composant ne calcule plus d'entropie : il n'a rien à en dire.
              onSnapshot={(snap) => {
                snapshotRef.current = snap;
              }}
            />

            <OrbitControls
              ref={orbitRef}
              enableZoom={true}
              enablePan={true}
              minDistance={5}
              maxDistance={100}
              target={[0, 0, 0]}
            />
          </Canvas>
        </div>
      </div>
    </div>
  );
}
